/* eslint-disable max-lines */
import { AxiosStatic, isAxiosError } from "axios";
import { AnsweredQuestionWithId, FileAnswer, OhCaseDocumentFile } from "@teamprevent/common/questionnaires/model";
import { MedicalTypeId } from "@teamprevent/common/healthSurveillance/models";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { parseTaskModel } from "../task/model";
import { ClinicalNotesAutoSaveDto } from "../clinicalNotes/models";

import { HealthSurveillanceAutoSaveDto, HealthSurveillanceQuestionnaireAutoSaveDto, SubmitClinicHealthSurveillanceQuestionnaireResponseDto } from "./models";

export const getHealthSurveillanceQuestionnaireEvidenceApi = (ohCaseId: string, customerId: string) => {
    return `/api/healthSurveillanceQuestionnaires/${ohCaseId}/${customerId}/evidence`;
};

/* eslint-disable jsdoc/require-param, jsdoc/require-returns */
/**
 * @deprecated Replaced by getHealthSurveillanceQuestionnaireEvidenceApi
 */
export const getHealthSurveillanceQuestionnaireEvidenceUploadApi = (ohCaseId: string, customerId: string) => {
    return `/api/healthSurveillanceQuestionnaires/${ohCaseId}/${customerId}/evidence`;
};

/**
 * @deprecated Replaced by getHealthSurveillanceQuestionnaireEvidenceApi
 */
export const getHealthSurveillanceQuestionnaireEvidenceDeleteApi = (ohCaseId: string) => {
    return `/api/healthSurveillanceQuestionnaires/${ohCaseId}/evidence`;
};
/* eslint-enable jsdoc/require-param, jsdoc/require-returns */

export const getHealthSurveillanceConsultationEvidenceApi = (clinicId: string, appointmentId: string, customerId: string) => {
    return `/api/clinic/${clinicId}/${appointmentId}/consultation/${customerId}/evidence`;
};

export const getOhCaseDocumentDownloadLink = (ohCaseId: string | undefined, fileAnswer: FileAnswer) => {
    // Using BlobId here for legacy OhCases where we stored the OhCaseDocumentId in the BlobId field
    return `/api/case/${ohCaseId}/document/${fileAnswer.ohCaseDocumentId || fileAnswer.blobId}?fileName=${fileAnswer.fileName}`;
};

export class HealthSurveillanceApi {
    private axios: AxiosStatic;

    constructor(ax: AxiosStatic) {
        this.axios = ax;
    }

    public async loadQuestionnaireByAppointmentId(clinicId: string, appointmentId: string): Promise<HealthSurveillanceQuestionnaireAutoSaveDto | null> {
        try {
            const response = await this.axios.get(`/api/clinic/${clinicId}/${appointmentId}/questionnaire`);
            return response.data as HealthSurveillanceQuestionnaireAutoSaveDto;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response && ex.response.status === 404) {
                return null;
            }

            throw ex;
        }
    }

    public async loadConsultationByAppointmentId(
        clinicId: string, appointmentId: string, abort: AbortController // eslint-disable-line @typescript-eslint/unified-signatures
    ): Promise<HealthSurveillanceQuestionnaireAutoSaveDto | null>;

    public async loadConsultationByAppointmentId(clinicId: string, appointmentId: string, abort?: AbortController):
        Promise<HealthSurveillanceQuestionnaireAutoSaveDto | null> {
        try {
            const response = await this.axios.get(`/api/clinic/${clinicId}/${appointmentId}/consultation`,
                { signal: abort?.signal });

            return response.data as HealthSurveillanceQuestionnaireAutoSaveDto;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response && ex.response.status === 404) {
                return null;
            }

            throw ex;
        }
    }

    public async loadRepeatMedicalConsultationByTaskId(
        clinicId: string, appointmentId: string, taskId: string, abort?: AbortController // eslint-disable-line @typescript-eslint/unified-signatures
    ): Promise<HealthSurveillanceQuestionnaireAutoSaveDto | null>;

    public async loadRepeatMedicalConsultationByTaskId(clinicId: string, appointmentId: string, taskId: string, abort?: AbortController):
        Promise<HealthSurveillanceQuestionnaireAutoSaveDto | null> {
        try {
            const response = await this.axios.get(`/api/clinic/${clinicId}/${appointmentId}/consultation/${taskId}/repeatMedical`,
                { signal: abort?.signal });
            return response.data as HealthSurveillanceQuestionnaireAutoSaveDto;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response && ex.response.status === 404) {
                return null;
            }

            throw ex;
        }
    }

    public async loadConsultationByTaskId(
        ohCaseId: string, taskId: string, abort: AbortController // eslint-disable-line @typescript-eslint/unified-signatures
    ): Promise<HealthSurveillanceAutoSaveDto | null>;

    public async loadConsultationByTaskId(ohCaseId: string, taskId: string, abort?: AbortController): Promise<HealthSurveillanceAutoSaveDto | null> {
        try {
            const response = await this.axios.get<HealthSurveillanceAutoSaveDto>(`/api/healthSurveillanceConsultation/${ohCaseId}/${taskId}`,
                { signal: abort?.signal });

            return response.data;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response && ex.response.status === 404) {
                return null;
            }

            throw ex;
        }
    }

    public async loadQuestionnaireByTaskId(ohCaseId: string, taskId: string): Promise<HealthSurveillanceAutoSaveDto | null> {
        try {
            const response = await this.axios.get<HealthSurveillanceAutoSaveDto>(`/api/healthSurveillanceQuestionnaire/${ohCaseId}/${taskId}`);
            return response.data;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                return null;
            }

            throw ex;
        }
    }

    public async getMedicalTypesForAppointment(clinicId: string, appointmentId: string): Promise<MedicalTypeId[]> {
        const response = await this.axios.get<MedicalTypeId[]>(`/api/clinic/${clinicId}/${appointmentId}/medicalTypes`);
        return response.data;
    }

    public async getMedicalTypesForOhCase(customerId: string, ohCaseId: string): Promise<MedicalTypeId[]> {
        const response = await this.axios.get<MedicalTypeId[]>(`/api/case/${customerId}/${ohCaseId}/medicalTypes`);
        return response.data;
    }

    public async submitQuestionnaireForAppointment(clinicId: string, appointmentId: string, questions: AnsweredQuestionWithId[]) {
        const response = await this.axios.post<SubmitClinicHealthSurveillanceQuestionnaireResponseDto>(
            `/api/clinic/${clinicId}/${appointmentId}/questionnaire`, { questions });
        return response.data;
    }

    public async submitQuestionnaireForTask(ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[]) {
        const response = await this.axios.post<TaskModel[]>(`/api/healthSurveillanceQuestionnaire/${ohCaseId}/${taskId}`, { questions });
        return response.data.map(parseTaskModel);
    }

    public async saveConsultationForAppointment(clinicId: string, appointmentId: string, questions: AnsweredQuestionWithId[], eTag: string | null) {
        try {
            const response = await this.axios.post<ClinicalNotesAutoSaveDto>(
                `/api/clinic/${clinicId}/${appointmentId}/consultation`, { data: { questions }, eTag });
            return response?.data;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    // eslint-disable-next-line max-len
    public async saveRepeatMedicalConsultationForAppointment(clinicId: string, appointmentId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string | null) {
        try {
            const response = await this.axios.post<ClinicalNotesAutoSaveDto>(
                `/api/clinic/${clinicId}/${appointmentId}/consultation/${taskId}/repeatMedical`, { data: { questions }, eTag });
            return response?.data;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async submitConsultationForAppointment(
        clinicId: string,
        appointmentId: string,
        isHavsTierThreeOrFour?: boolean): Promise<SubmitClinicHealthSurveillanceQuestionnaireResponseDto> {
        const response = await this.axios.post<SubmitClinicHealthSurveillanceQuestionnaireResponseDto>(
            `/api/clinic/${clinicId}/${appointmentId}/consultation/complete?isHavsTierThreeOrFour=${isHavsTierThreeOrFour}`);
        return response.data;
    }

    public async submitRepeatMedicalConsultationForAppointment(
        clinicId: string,
        appointmentId: string,
        taskId: string,
        isHavsTierThreeOrFour?: boolean): Promise<SubmitClinicHealthSurveillanceQuestionnaireResponseDto> {
        const response = await this.axios.post<SubmitClinicHealthSurveillanceQuestionnaireResponseDto>(
            `/api/clinic/${clinicId}/${appointmentId}/consultation/${taskId}/repeatMedical/complete?isHavsTierThreeOrFour=${isHavsTierThreeOrFour}`);
        return response.data;
    }

    public async saveConsultationForTask(ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string | null) {
        try {
            const response = await this.axios.post<ClinicalNotesAutoSaveDto>(
                `/api/healthSurveillanceConsultation/${ohCaseId}/${taskId}`, { data: { questions }, eTag });
            return response?.data;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async submitConsultationForTask(ohCaseId: string, taskId: string, isHavsTierThreeOrFour?: boolean) {
        const response = await this.axios.post<TaskModel[]>(
            `/api/healthSurveillanceConsultation/${ohCaseId}/${taskId}/complete?isHavsTierThreeOrFour=${isHavsTierThreeOrFour}`);
        return response.data.map(parseTaskModel);
    }

    /* eslint-disable jsdoc/require-param, jsdoc/require-returns, deprecation/deprecation */
    /**
     * @deprecated
     */
    public async uploadDocumentForQuestionnaire(ohCaseId: string, customerId: string, file: File): Promise<OhCaseDocumentFile> {
        const data = new FormData();
        data.append("file", file);

        const response = await this.axios.post<OhCaseDocumentFile>(getHealthSurveillanceQuestionnaireEvidenceUploadApi(ohCaseId, customerId), data);
        return response.data;
    }

    /**
     * @deprecated No longer used, only used by deprecated components
     */
    public async uploadHealthSurveillanceConsultationDocument(
        clinicId: string,
        appointmentId: string,
        customerId: string,
        file: File): Promise<OhCaseDocumentFile>
    {
        const data = new FormData();
        data.append("file", file);

        const response = await this.axios.post<OhCaseDocumentFile>(getHealthSurveillanceConsultationEvidenceApi(clinicId, appointmentId, customerId), data);
        return response.data;
    };

    /**
     * @deprecated Replaced by deleteEmployeeQuestionnaireEvidence
     */
    public async deleteDocumentForQuestionnaire(ohCaseId: string,ohCaseDocumentId: string) {
        await this.axios.delete<void>(`${getHealthSurveillanceQuestionnaireEvidenceDeleteApi(ohCaseId)}/${ohCaseDocumentId}`);
    }
    /* eslint-enable jsdoc/require-param, jsdoc/require-returns, deprecation/deprecation */

    public async deleteEmployeeQuestionnaireEvidence(ohCaseId: string, customerId: string, ohCaseDocumentId: string) {
        await this.axios.delete<void>(`${getHealthSurveillanceQuestionnaireEvidenceApi(ohCaseId, customerId)}/${ohCaseDocumentId}`);
    }

    /* eslint-disable jsdoc/require-param, jsdoc/require-returns, deprecation/deprecation */
    /**
     * @deprecated Replaced by deleteConsultationEvidence
     */
    public async deleteDocumentForConsultation(ohCaseId: string, ohCaseDocumentId: string) {
        await this.axios.delete<void>(`/api/healthSurveillanceConsultation/${ohCaseId}/evidence/${ohCaseDocumentId}`);
    }
    /* eslint-enable jsdoc/require-param, jsdoc/require-returns, deprecation/deprecation */

    public async deleteConsultationEvidence(ohCaseId: string, customerId: string, ohCaseDocumentId: string) {
        await this.axios.delete<void>(`/api/healthSurveillanceConsultation/${ohCaseId}/${customerId}/evidence/${ohCaseDocumentId}`);
    }

    public async sendQuestionnaire(customerId: string, ohCaseId: string, taskId: string, consultationRequired?: boolean) {
        const response = await this.axios.post<
            ServerModelWithDateStrings<TaskModel>[]
        >(`/api/customer/${customerId}/ohCase/${ohCaseId}/sendHealthSurveillanceQuestionnaire/${taskId}`, null, {
            params: consultationRequired ? { consultationRequired } : null,
        });
        return response.data.map(parseTaskModel);
    }

    public async completeNowHealthSurveillanceQuestionnaire(customerId: string, ohCaseId: string, sendQuestionnaireTaskId: string) {
        try {
            const response = await this.axios.post<TaskModel[]>(
                `/api/customer/${customerId}/ohCase/${ohCaseId}/completeNowHealthSurveillanceQuestionnaire/${sendQuestionnaireTaskId}`);
            return response.data.map(parseTaskModel);
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                throw new Error(ex.response.data);
            }

            throw ex;
        }
    }

    public async saveHavsClinicalAssessmentOfHistoryAndExamination(ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string | null) {
        try {
            const response = await this.axios.post<ClinicalNotesAutoSaveDto>(
                `/api/healthSurveillanceConsultation/${ohCaseId}/${taskId}`, { data: { questions }, eTag });
            return response?.data;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async submitHavsClinicalAssessmentOfHistoryAndExamination(ohCaseId: string, taskId: string) {
        try {
            const response = await this.axios.post<TaskModel[]>(
                `/api/healthSurveillanceConsultation/${ohCaseId}/${taskId}/havsExamination/complete`);
            return response.data.map(parseTaskModel);
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                throw new Error(ex.response.data);
            }

            throw ex;
        }
    }

    public async actionFurtherInformationAndReviewOfFitness(customerId: string, ohCaseId: string, taskId: string, notes: string) {
        try {
            const response = await this.axios.post<TaskModel[]>(
                `/api/customer/${customerId}/ohCase/${ohCaseId}/task/${taskId}/actionFurtherInformationAndReviewOfFitness`, { notes });
            return response.data.map(parseTaskModel);
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                throw new Error(ex.response.data);
            }

            throw ex;
        }
    }

    public async dnaCloseCase(customerId: string, ohCaseId: string) {
        try {
            const response = await this.axios.post(
                `/api/customer/${customerId}/ohCase/${ohCaseId}/dnaCloseHealthSurveillanceCase`);
            return response.data;
        } catch (ex) {
            if (isAxiosError(ex) && ex.response?.status === 400) {
                throw new Error(ex.response.data);
            }

            throw ex;
        }
    }
}
